<template>
  <div class="map" id="chart-map">
    <layer-switcher-component
      :activeOverlaySelector="false"
      :activeBaseLayer="checkedBaseLayer"
      :onBaseLayerChange="changeBaseLayer"
      :baseLayers="baseLayers"
      :coherenceIntervals="$conf.map.layers.coherenceIntervals"
      class="map-lsw">
    </layer-switcher-component>
    <a href="#"
      v-if="dates.indexOf(date) > 0"
      class="rotate prev"
      @click="e => prevDate(e)">
      <b-icon icon="chevron-left-circle" type="is-black" size="is-large"></b-icon>
    </a>
    <a href="#"
      v-if="dates.indexOf(date) < dates.length - 1"
      class="rotate next"
      @click="e => nextDate(e)">
      <b-icon icon="chevron-right-circle" type="is-black" size="is-large"></b-icon>
    </a>
    <div id="chart-map-statusbar" class="has-background-black-ter">
      <span class="copyrights" v-if="$conf.chart.copyrights.length" v-html="copyrights"></span>
    </div>
    <img id="legend" ref="legend" v-if="legend" :src="legend"/>
  </div>
</template>

<script>

import { createStyle } from 'util/layer/StyleBuilder'
import { deepCopy } from 'util/helpers'
import MapEngine from 'util/MapEngine'
import LayerSwitcherComponent from 'comp/map/LayerSwitcher'

/**
 * App component
 */
export default {
  name: 'ChartImageryComponent',
  components: {
    LayerSwitcherComponent
  },
  props: {
    parcel: {
      type: Object,
      default: null
    },
    date: {
      type: String,
      default: null
    },
    dates: {
      type: Array,
      default: []
    }
  },
  data () {
    return {
      me: null,
      layers: [],
      activeBaseLayerId: Object.keys(this.$conf.chart.layers[this.$store.getters['chartImageryTsGroup']])[0],
      tileSetLayer: null,
      legend: null,
      group: this.$store.getters['chartImageryTsGroup']
    }
  },
  computed: {
    checkedBaseLayer () {
      return this.$store.getters['config'].chart.imageryDefaults[this.group]
    },
    baseLayers () {
      return this.$store.getters['config'].chart.layers[this.group]
    },
    OSMLayer () {
      return this.$store.getters['config'].map.layers.overlays['osm']
    },
    copyrights () {
      // Select copyrights based on the selected layer group
      let copyrightEntries = []

      this.$conf.chart.copyrights.map(item => {
        if (item.hasOwnProperty('group') && item.group === this.$store.getters['chartImageryTsGroup']) {
          let content = item.url ? `<a href="${ item.url }" target="_blank">${ item.text }</a>` : item.text
          copyrightEntries.push(content)
        }
      })
      return copyrightEntries.join(' | ')
    }
  },
  mounted () {
    const mapConf = deepCopy(this.$conf.map)
    mapConf.el = '#chart-map'
    mapConf.proj4defs = null
    mapConf.layers = []
    mapConf.interactions = []
    this.me = new MapEngine(mapConf)
    this.layers.base = this.me.createLayer({
      type: 'Group',
      layers: []
    })
    this.layers.overlay = this.me.createLayer({
      type: 'Group',
      layers: []
    })
    Object.keys(this.baseLayers).forEach(key => {
      // sentinel hub layers and kappazeta layers
      if ('TIME' in this.baseLayers[key].params) {
        this.baseLayers[key].params.TIME = `${this.date}/${this.date}`
      // maa-amet layers
      } else if ('date' in this.baseLayers[key].params) {
        this.baseLayers[key].params.date = this.date
      }
    })
    this.createOSMLayer(this.OSMLayer)
    this.createBaseLayers(this.baseLayers, this.activeBaseLayerId)
    this.createMap()
    this.me.fitTo({
      bbox: this.me.bbox(this.parcel.geometry),
      srid: this.$conf.api.srid || 'EPSG:4326'
    })
    this.tileSetLayer = this.createTileSetLayer(this.$store.getters['tileSetConf'])
    this.me.addLayer(this.tileSetLayer, this.layers.overlay)
    this.changeBaseLayer(this.activeBaseLayerId)
  },
  updated () {
    this.layers.base.getLayers().forEach(layer => {
      const params = layer.getSource().getParams()
      // sentinel hub layers and kappazeta layers
      if ('TIME' in params) {
        params.TIME = `${this.date}/${this.date}`
      // maa-amet layers
      } else if ('date' in params) {
        params.date = this.date
      }
      layer.getSource().updateParams(params)
    })
  },
  methods: {
    createMap () {
      this.me.set({
        layers: Object.keys(this.layers).map(i => this.layers[i])
      })
    },
    createBaseLayers (layers, activeBaseLayerId) {
      Object.keys(layers).forEach(id => {
        layers[id].visible = (id === activeBaseLayerId)
        layers[id].id = id
        const layer = this.me.createLayer(layers[id])
        this.me.addLayer(layer, this.layers.base)
      })
    },
    createOSMLayer (layer) {
      const osml = this.me.createLayer(layer)
      osml.visible = true
      this.me.addLayer(osml, this.layers.overlay)
    },
    createTileSetLayer (conf) {
      const styleConf = deepCopy(this.$conf.tilesets.style.searchResults)
      styleConf.stroke.width = 2
      const styleBgConf = deepCopy(this.$conf.tilesets.style.searchResults)
      styleBgConf.stroke.width = 4
      styleBgConf.stroke.color = 'grey'
      const selectedStyle = createStyle([styleBgConf, styleConf])
      const selectedId = this.parcel[this.$conf.search[this.$store.getters['searchIndex']].id]
      const layerConf = {
        type: 'MVT',
        id: conf.id,
        title: 'Tile set',
        projection: 'EPSG:3857',
        url: this.$conf.tilesets.url + this.$conf.tilesets.key + '/' + conf.id + '/{z}/{x}/{y}.' + conf.format,
        extent: this.$map.transform({ bbox: conf.bounds, from: 'EPSG:4326' }),
        style: (feature, resolution, buildStyle) => {
          // selected
          if (feature.get('id') === selectedId) {
            return selectedStyle
          }
          return null
        },
        tileGrid: {
          minZoom: conf.minzoom,
          maxZoom: conf.maxzoom
        }
      }
      return this.me.createLayer(layerConf)
    },
    changeBaseLayer (layerid) {
      this.layers.base.getLayers().forEach(layer => {
        if (layer.get('id') === layerid) {
          const url = layer.values_.conf.url
          const resolution = this.me.get().getView().getResolution()
          const layerName = layer.getSource().params_.LAYERS
          if (url.includes("maaamet")) {
            const graphicUrl = `${url}?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetLegendGraphic&FORMAT=image%2Fpng&LAYER=${layerName}&SCALE=${resolution}&SLD_VERSION=1.1.0`
            this.legend = graphicUrl
          } else if (url.includes("sentinel-hub")) {
            // Don't use legend for TCI and False Color Images
          }
          layer.setVisible(true)
        } else {
          layer.setVisible(false)
        }
      })
    },
    prevDate (e) {
      e.preventDefault()
      const i = this.dates.indexOf(this.date)
      this.$store.dispatch('chartImageryDate', this.dates[i - 1])
    },
    nextDate (e) {
      e.preventDefault()
      const i = this.dates.indexOf(this.date)
      this.$store.dispatch('chartImageryDate', this.dates[i + 1])
    }
  }
}



</script>

<style lang="stylus">
.chart-lsw
  position: absolute
  top: 10px
  right: 10px
  z-index: 200
.rotate
  position: absolute
  top: 45%
  z-index: 2
  opacity: .8
  &.prev
    left: 20px
  &.next
    right: 20px
#chart-map-statusbar
  position: absolute
  z-index: 3
  right: 0
  bottom: 0
  padding: 2px 10px 2px 10px
  border-radius: 4px 0 0 0
  font-size: .8rem
  color: white
  a
    color: white
#legend
  position: absolute
  z-index: 3
  bottom: 0
  max-width: 200px
.map-lsw
  position: absolute
  top: 10px
  right: 10px
  z-index: 1004
</style>
